import axios from "@/plugins/axios";

import BaseService from "@/services/BaseService";
import { useGlobalStateStore } from "@/stores/global";

export default class AuthService extends BaseService {

	private globalStateStore;

	constructor () {
		super();
		this.url = "/";
		this.setupAPI(axios.defaults.baseURL);
		this.globalStateStore = useGlobalStateStore();
	}

	/**
	 * Calling /sanctum/csrf-cookie sets the XSRF-TOKEN cookie required for Laravel’s CSRF protection to pass VerifyCsrfToken.
	 * Without it, even valid login attempts will result in 419 Page Expired
	 *
	 * Call sanctum/csrf-cookie only once per session, just before the first stateful authentication request (e.g., login, or
	 * any other request relying on session cookies for authentication).
	 */
	async getCsrfCookie () {
		return this.get(route("sanctum.csrf-cookie"));
	}

	/**
	 * Do a head request to see if the user is logged in or not.
	 * @returns void. We are interested in the status codes of the associated
	 * response: 401 for not logged in or 200 if logged in.
	 */
	async state (): Promise<void> {
		await this.head(route("api.user.index"));
		return;
	}

	async getCurrentUser () {
		return this.get("/api/user");
	}

	async forgotPassword (payload: any) {
		this.globalStateStore.loadingElements.forgotPasswordForm = true;
		return this.post(route("password.email"), payload).finally(() => {
			this.globalStateStore.loadingElements.forgotPasswordForm = false;
		});
	}

	async resetPassword (payload: any) {
		this.globalStateStore.loadingElements.resetPasswordForm = true;
		return this.post(route("password.update"), payload).finally(() => {
			this.globalStateStore.loadingElements.resetPasswordForm = false;
		});
	}

	async registerUser (payload: any) {
		this.globalStateStore.loadingElements.registerForm = true;
		return this.post("/register", payload).finally(() => {
			this.globalStateStore.loadingElements.registerForm = false;
		});
	}

	async updatePassword (payload: any) {
		this.globalStateStore.loadingElements.updatePasswordForm = true;
		return this.put("/user/password", payload).finally(() => {
			this.globalStateStore.loadingElements.updatePasswordForm = false;
		});
	}

	async sendVerification (payload: any) {
		console.log("sending verificaton link");
		this.globalStateStore.loadingElements.sendVerificationForm = true;
		return this.post("/email/verification-notification", payload).finally(() => {
			this.globalStateStore.loadingElements.sendVerificationForm = false;
		});
	}

	async updateUser (payload: any) {
		this.globalStateStore.loadingElements.updateProfileForm = true;
		return this.put("/user/profile-information", payload).finally(() => {
			this.globalStateStore.loadingElements.updateProfileForm = false;
		});
	}

	login (payload: any) {
		// Enable Laravel's 'remember me' functionality so that this client
		// can go offline, the session can expire, but it will then renew itself
		// when the client next connects.
		payload.remember = true;

		this.globalStateStore.loadingElements["loginForm"] = true;

		return this.post("/login", payload, { withCredentials: true }).finally(() => {

			this.globalStateStore.loadingElements["loginForm"] = false;
		});
	}

	logout () {
		this.globalStateStore.loadingElements.logoutForm = true;
		return this.post(route("logout"), {}).finally(() => {
			this.globalStateStore.loadingElements.logoutForm = false;
		});
	}

	reauthenticateUser (payload: any) {
		return this.post("/reauthenticate", payload);
	}

	logoutOtherSessions (password: string) {
		return this.post("/session", { password: password });
	}

	checkSession () {
		return this.get("/api/check-session", { withCredentials: true });
	}

}
